import {
  Dehaze,
} from '@mui/icons-material'
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
//* NOTE: MomentJS Locales
import 'moment/dist/locale/es'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/pt'
import 'moment/dist/locale/pt-br'
import 'moment/dist/locale/sk'
import { NavLink, useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

import logo from '~/assets/ISOLOGO-03.svg'
import { axios } from '~/axios'
import PlatformLanguageSelector from '~/components/LanguageSelector'
import NotificationsMenu from '~/components/header/NotificationsMenu'
import SidebarMenu, {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
} from '~/features/navigation/components/SidebarMenu'
import { useMenuTabs } from '~/features/navigation/hooks/useMenuTabs'
import { getMenuItemPath } from '~/features/navigation/utils/menu-utils'
import { updateUser } from '~/redux/slices/user'
import { logout } from '~/services/auth-services'
import { ROLES, canAccess, getHomePage } from '~/utils/role-utils'

import { useAppDispatch, useAppSelector } from './redux/hooks'
import { op } from './utils/open-panel-utils'

interface HeaderProps {
  sidebarCollapsed?: boolean
  onToggleSidebar?: () => void
}

export default function Header({
  sidebarCollapsed = false,
  onToggleSidebar = () => {},
}: HeaderProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const intercom = useIntercom()
  const user = useAppSelector(state => state.user)

  const queryClient = useQueryClient()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarHovered, setIsSidebarHovered] = useState(false)
  const showNotifications =
    user?.role === ROLES.PS_TEAM || user?.role === ROLES.HUB_ADMIN

  const tabs = useMenuTabs()

  async function handleLogout() {
    try {
      if (!user) return
      await logout()
    } finally {
      navigate('/login')
      dispatch(updateUser(null))
      void queryClient.resetQueries()

      intercom.update({
        name: '',
        email: '',
        userId: '',
      })

      op.clear()
      Sentry.setUser(null)

      // NOTE: Reset axios headers (in case of using the dynamic link)
      axios.defaults.headers.post['Stoplight-Client-Id'] = 'stoplight-web'
    }
  }

  if (!user) return null

  const mainLogoURL = canAccess(user, 'dashboard')
    ? 'homepage'
    : getHomePage(user)

  return (
    <>
      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #edf0f3',
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          disableGutters
          sx={{ minHeight: '72px !important', px: { xs: 1.5, md: 3 } }}
        >
          <IconButton
            onClick={() => {
              setIsSidebarOpen(true)
            }}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
            aria-label={t('views.toolbar.platform')}
          >
            <Dehaze color="primary" />
          </IconButton>

          <Stack
            component={NavLink}
            to={getMenuItemPath(mainLogoURL, user)}
            direction="row"
            alignItems="center"
            justifyContent="center"
            width={{
              xs: 'auto',
              md: sidebarCollapsed && !isSidebarHovered
                ? SIDEBAR_COLLAPSED_WIDTH
                : SIDEBAR_WIDTH,
            }}
            flexShrink={0}
            sx={{
              ml: { xs: 0, md: -3 },
              textDecoration: 'none',
              transition: theme =>
                theme.transitions.create('width', {
                  duration: 350,
                  easing: theme.transitions.easing.easeInOut,
                }),
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Stoplight Logo"
              sx={{ width: 52, height: 52, objectFit: 'contain' }}
            />
          </Stack>

          <Stack direction="row" ml="auto" alignItems="center" gap={2}>
            <PlatformLanguageSelector />

            {showNotifications && <NotificationsMenu />}
          </Stack>
        </Toolbar>
      </AppBar>
      <SidebarMenu
        tabs={tabs}
        user={user}
        mobileOpen={isSidebarOpen}
        onMobileClose={() => {
          setIsSidebarOpen(false)
        }}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={onToggleSidebar}
        onCollapsedHoverChange={setIsSidebarHovered}
      />
    </>
  )
}
